<template>
  <div class="contract-wrapper">
    <v-skeleton-loader v-if="loading" type="image" height="160" class="rounded-xl custom-skeleton mb-2"></v-skeleton-loader>

    <div
      v-else-if="!isVerifiedOrCenter"
      class="rounded-xl pa-6 d-flex align-center justify-center text-center"
      style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); min-height: 160px">
      <div>
        <v-avatar color="rgba(255,152,0,0.15)" size="50" class="mb-3">
          <v-icon color="orange lighten-1" large>mdi-email-lock</v-icon>
        </v-avatar>
        <h3 class="white--text subtitle-1 font-weight-bold mb-1">Verifizierung erforderlich</h3>
        <p class="grey--text text--lighten-1 caption mb-3" style="max-width: 280px; margin: 0 auto; line-height: 1.4">
          Bitte verifiziere deine E-Mail-Adresse, um Einsicht in deine laufenden Verträge und dein Guthaben zu erhalten.
        </p>
        <v-btn color="primary" small rounded class="px-4" elevation="0" @click="showVerifyDialog = true"> Jetzt verifizieren </v-btn>
      </div>
    </div>

    <div v-else-if="sortedContracts.length > 0">
      <v-carousel
        v-model="carouselModel"
        :show-arrows="false"
        hide-delimiter-background
        delimiter-icon="mdi-circle-small"
        :hide-delimiters="sortedContracts.length <= 1"
        height="auto"
        class="contract-slider rounded-xl elevation-10"
        :continuous="false"
        @change="onSlideChange">
        <v-carousel-item v-for="(contract, i) in sortedContracts" :key="i">
          <v-card class="contract-card rounded-xl overflow-hidden" :class="getCardClass(contract)" @click="toggleExpand(contract)" v-ripple>
            <div class="card-shine"></div>
            <div class="card-mesh"></div>

            <div class="pa-4 relative z-index-2">
              <div class="d-flex justify-space-between align-start">
                <div class="d-flex flex-column" style="width: 70%">
                  <div class="d-flex align-center mb-2 flex-wrap">
                    <v-chip x-small :color="getStatusColor(contract)" class="font-weight-black white--text px-2 backdrop-blur border-light mr-2 mb-1">
                      <v-icon x-small left>{{ getStatusIcon(contract) }}</v-icon>
                      {{ getStatusLabel(contract) }}
                    </v-chip>
                  </div>

                  <h3 class="contract-title white--text font-weight-black text-uppercase text-shadow-sm mb-1">
                    {{ formatTitle(contract.title) }}
                  </h3>

                  <div class="d-flex align-center mt-1">
                    <v-icon x-small color="rgba(255,255,255,0.7)" class="mr-1">mdi-calendar-range</v-icon>
                    <span class="white--text font-weight-bold text-caption opacity-90"> {{ contract.start }} - {{ contract.end }} </span>
                  </div>

                  <div v-if="contract.addons.length > 0 && !expanded" class="mt-3 d-flex align-center fade-in">
                    <div v-for="(addon, idx) in contract.addons.slice(0, 5)" :key="idx" class="addon-icon-mini mr-1" :title="addon.name">
                      <v-icon x-small color="white">{{ getAddonIcon(addon.name) }}</v-icon>
                    </div>
                    <span v-if="contract.addons.length > 5" class="caption white--text opacity-70 ml-1">+{{ contract.addons.length - 5 }}</span>
                  </div>
                </div>

                <div class="balance-wrapper text-right pl-2" v-if="contract.active">
                  <div class="balance-circle">
                    <span class="caption d-block grey--text text--lighten-3 font-weight-bold" style="font-size: 0.55rem; letter-spacing: 0.5px"
                      >SALDO</span
                    >
                    <span class="font-weight-black white--text text-h6 leading-none">
                      {{ formatBalance(balance) }}<span class="caption font-weight-bold">.{{ formatDecimals(balance) }}</span>
                    </span>
                  </div>

                  <div class="expand-indicator mt-2">
                    <v-icon color="white" size="24" class="chevron-anim">
                      {{ openProfileOnClick ? "mdi-chevron-right" : expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                    </v-icon>
                  </div>
                </div>

                <div v-else class="balance-wrapper text-right pt-2">
                  <v-avatar size="45" color="rgba(255,255,255,0.1)">
                    <v-icon color="white">{{ contract.future ? "mdi-rocket-launch" : "mdi-history" }}</v-icon>
                  </v-avatar>

                  <div class="expand-indicator mt-2">
                    <v-icon color="white" size="24" class="chevron-anim">
                      {{ openProfileOnClick ? "mdi-chevron-right" : expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                    </v-icon>
                  </div>
                </div>
              </div>
            </div>

            <v-expand-transition>
              <div v-if="expanded && !openProfileOnClick" class="card-details px-4 pb-5 relative z-index-2">
                <v-divider class="my-3 border-opacity-20"></v-divider>

                <div class="d-flex justify-space-between mb-5 px-1">
                  <div v-if="contract.active" class="action-item" @click.stop="$emit('timestop')">
                    <div class="action-btn secondary-glass mb-2">
                      <v-icon color="white">mdi-pause</v-icon>
                    </div>
                    <span>Abo pausieren</span>
                  </div>
                  <div class="action-item" @click.stop="$emit('healthcare')">
                    <div class="action-btn health-glass mb-2">
                      <v-icon color="white">mdi-hospital-box-outline</v-icon>
                    </div>
                    <span>KK Bestätigung</span>
                  </div>
                </div>

                <div class="addon-section mb-4">
                  <div class="caption font-weight-bold white--text mb-2 text-uppercase opacity-70 d-flex justify-space-between align-center">
                    <span>Dein Abo & Pakete</span>
                    <v-progress-circular v-if="loadingUpgrades" indeterminate width="2" size="14" color="white"></v-progress-circular>
                  </div>

                  <v-row class="addon-grid mt-2 justify-space-between" no-gutters>
                    <v-col cols="6" v-for="(addon, idx) in contract.addons" :key="'own' + idx" class="addon-chip active-addon glass-inner-card pa-1">
                      <div class="addon-icon-box active mr-3">
                        <v-icon small color="white">{{ getAddonIcon(addon.name) }}</v-icon>
                      </div>
                      <div class="d-flex flex-column overflow-hidden" style="flex: 1">
                        <span class="white--text font-weight-bold caption text-truncate">{{ addon.name }}</span>
                        <span class="green--text text--accent-3 caption font-weight-bold" style="font-size: 0.6rem">
                          Aktiv <v-icon x-small color="green accent-3">mdi-check</v-icon></span
                        >
                      </div>
                    </v-col>

                    <template v-if="loadingUpgrades">
                      <v-col cols="6" v-for="n in 2" :key="'skel' + n" class="addon-chip skeleton-chip">
                        <v-skeleton-loader type="avatar" class="mr-2" height="16" width="16"></v-skeleton-loader>
                        <v-skeleton-loader type="text" width="60%"></v-skeleton-loader>
                      </v-col>
                    </template>

                    <template v-else-if="upgradeData">
                      <v-col
                        cols="6"
                        v-for="(pck, idx) in availableUpgrades"
                        :key="'upg' + idx"
                        class="addon-chip upgrade-addon pa-1"
                        @click.stop="triggerQuickUpgrade(pck)"
                        v-ripple>
                        <div class="addon-icon-box upgrade mr-3">
                          <v-icon small color="grey lighten-1">{{ getAddonIcon(pck.packageName) }}</v-icon>
                          <div class="plus-badge"><v-icon x-small color="white">mdi-plus</v-icon></div>
                        </div>
                        <div class="d-flex flex-column overflow-hidden" style="flex: 1">
                          <span class="grey--text text--lighten-1 font-weight-bold caption text-truncate">{{ pck.packageName }}</span>
                          <span class="primary--text caption font-weight-bold" style="font-size: 0.6rem">Dazu buchen</span>
                        </div>
                      </v-col>
                    </template>
                  </v-row>
                </div>

                <div class="detail-box pa-3 rounded-lg glass-inner-card mt-3">
                  <div class="caption font-weight-bold white--text mb-2 text-uppercase opacity-70">Deine Laufzeit</div>

                  <div v-for="(period, i) in contract.details.mainPeriods" :key="'main' + i" class="timeline-row pa-2 rounded-lg mb-1">
                    <div class="icon-fix mr-3">
                      <v-icon x-small color="white">mdi-card-account-details-outline</v-icon>
                    </div>
                    <div class="d-flex flex-column">
                      <span class="caption white--text opacity-70 row-label">
                        Basis-Laufzeit {{ contract.details.mainPeriods.length > 1 ? i + 1 : "" }}
                      </span>
                      <span class="font-weight-bold white--text row-value"> {{ period.start }} - {{ period.end }} </span>
                    </div>
                  </div>

                  <div v-for="(idle, i) in contract.details.idlePeriods" :key="'idle' + i" class="timeline-row pa-2 rounded-lg mb-1">
                    <div class="icon-fix mr-3">
                      <v-icon x-small color="grey lighten-1">mdi-pause-circle-outline</v-icon>
                    </div>
                    <div class="d-flex flex-column">
                      <span class="caption grey--text text--lighten-1 row-label">Pause (Timestop)</span>
                      <span class="font-weight-bold grey--text text--lighten-1 row-value"> {{ idle.start }} - {{ idle.end }} </span>
                    </div>
                  </div>

                  <div v-for="(tsBonus, i) in contract.details.timeStopBonuses" :key="'ts' + i" class="timeline-row pa-2 rounded-lg mb-1">
                    <div class="icon-fix mr-3">
                      <v-icon x-small color="blue lighten-2">mdi-clock-time-four-outline</v-icon>
                    </div>
                    <div class="d-flex flex-column">
                      <span class="caption blue--text text--lighten-2 row-label">Verlängerung (Timestop)</span>
                      <span class="font-weight-bold blue--text text--lighten-2 row-value"> {{ tsBonus.start }} - {{ tsBonus.end }} </span>
                    </div>
                  </div>

                  <div
                    v-for="(bonus, i) in contract.details.regularBonuses"
                    :key="'bon' + i"
                    class="timeline-row pa-2 rounded-lg mb-1 is-bonus elevation-3">
                    <div class="icon-fix mr-3">
                      <v-icon small color="amber accent-3">mdi-gift-outline</v-icon>
                    </div>
                    <div class="d-flex flex-column">
                      <span class="caption amber--text text--accent-3 font-weight-black text-uppercase row-label" style="letter-spacing: 0.5px">
                        Bonus
                      </span>
                      <span class="font-weight-bold white--text row-value"> {{ bonus.start }} - {{ bonus.end }} </span>
                    </div>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </v-card>
        </v-carousel-item>
      </v-carousel>
    </div>

    <verify-email @verifying="verifying = true" v-model="showVerifyDialog" @emailVerified="onEmailVerified"></verify-email>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { readOverview } from "@/store/homeOverviewStore"
import moment from "moment"
import VerifyEmail from "@/components/verification/verifyEmail.vue"

export default {
  name: "ActiveContractCard",
  components: {
    VerifyEmail,
  },
  props: {
    user: { type: Object, required: true },
    openProfileOnClick: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      hasSnapshot: false,
      loadVersion: 0,
      contracts: [],
      balance: null,
      expanded: false,
      carouselModel: 0,

      // Verifizierung
      isVerifiedOrCenter: true,
      showVerifyDialog: false,
      verifying: false,

      // Upgrade Logic
      loadingUpgrades: false,
      upgradeData: null,
      cachedUpgradeData: {},

      // Liste der Center-Accounts
      studioAccountsMap: [
        "frauenfeld@wellcomefit.ch",
        "wallisellen@wellcomefit.ch",
        "basel@wellcomefit.ch",
        "wetzikon@wellcomefit.ch",
        "dietlikon@wellcomefit.ch",
        "netstal@wellcomefit.ch",
        "schwanden@wellcomefit.ch",
        "niederurnen@wellcomefit.ch",
        "steckborn@wellcomefit.ch",
        "stgallenwest@wellcomefit.ch",
        "st.gallen@wellcomefit.ch",
        "einstein@wellcomefit.ch",
        "arbon@wellcomefit.ch",
        "obfelden@wellcomefit.ch",
        "herisau@wellcomefit.ch",
        "sirnach@wellcomefit.ch",
        "rikon@wellcomefit.ch",
        "oerlikon@wellcomefit.ch",
        "buelach@wellcomefit.ch",
        "winterthur@wintifit.ch",
        "pfungen@wintifit.ch",
      ],
    }
  },
  computed: {
    sortedContracts() {
      if (!this.contracts.length) return []
      return [...this.contracts].sort((a, b) => a.startDateObj - b.startDateObj)
    },
    // Filtert Pakete raus, die der Kunde schon hat
    availableUpgrades() {
      if (!this.upgradeData || !this.upgradeData.availablePackages) return []

      const currentContract = this.sortedContracts[this.carouselModel]
      if (!currentContract) return []

      const ownedNames = currentContract.addons.map((a) => a.name.toLowerCase())

      return this.upgradeData.availablePackages.filter((p) => {
        const pName = p.packageName.toLowerCase()
        return !ownedNames.some((owned) => owned.includes(pName) || pName.includes(owned))
      })
    },
  },
  watch: {
    "user._id": "loadCard",
    "user.id": "loadCard",
    "$store.state.studioStore.selectedStudio._id": "loadCard",
  },
  created() {
    this._cardAlive = true
    this.loadCard()
  },
  beforeDestroy() {
    this._cardAlive = false
    this.loadVersion++
  },
  methods: {
    contractScope() {
      const owner = userService.getUserId()
      const studio = this.$store.state.studioStore.selectedStudio?._id
      return owner && studio ? JSON.stringify([owner, studio, moment().format("YYYY-MM-DD")]) : null
    },
    isCurrent(context) {
      return this._cardAlive && context.version === this.loadVersion && context.scope === this.contractScope() && context.token === localStorage.getItem("token")
    },
    contractKey(contract) {
      return contract ? `${contract.id}:${Number(contract.startDateObj)}` : null
    },
    saveContracts() {
      if (!this.hasSnapshot || !this._cardAlive || this._cardScope !== this.contractScope()) return
      this.$store.commit("homeOverviewStore/save", {
        key: "contracts",
        scope: this._cardScope,
        data: {
          contracts: this.contracts.map((contract) => ({ ...contract, startDateObj: Number(contract.startDateObj), endDateObj: Number(contract.endDateObj) })),
          selectedKey: this.contractKey(this.sortedContracts[this.carouselModel]),
        },
      })
    },
    clearContractCache(context) {
      for (const key of ["contracts", "balance"]) this.$store.commit("homeOverviewStore/clear", { key, scope: context.scope })
      this.loadVersion++ // Also discard the other pending response after an authorization failure.
      this.contracts = []
      this.balance = null
      this.hasSnapshot = false
      this.loading = false
      this.expanded = false
      this.upgradeData = null
      this.$emit("no-contracts")
    },
    async loadCard() {
      if (!this._cardAlive) return
      const context = { version: ++this.loadVersion, scope: this.contractScope(), token: localStorage.getItem("token") }
      if (this._cardScope !== context.scope) {
        this._cardScope = context.scope
        this.contracts = []
        this.balance = null
        this.hasSnapshot = false
        this.carouselModel = 0
        this.expanded = false
        this.upgradeData = null
        const cached = readOverview(this.$store.state.homeOverviewStore, "contracts", context.scope)
        if (cached) {
          this.contracts = cached.contracts.map((contract) => ({ ...contract, startDateObj: moment(contract.startDateObj), endDateObj: moment(contract.endDateObj) }))
          this.hasSnapshot = true
          const index = this.sortedContracts.findIndex((contract) => this.contractKey(contract) === cached.selectedKey)
          this.carouselModel = index < 0 ? 0 : index
          this.$emit(this.contracts.length ? "contracts-loaded" : "no-contracts", true)
        }
        this.balance = readOverview(this.$store.state.homeOverviewStore, "balance", context.scope)
      }
      this.loading = !this.hasSnapshot
      try {
        const status = await api.fetchUserEmailVerificationStatus()
        if (!this.isCurrent(context)) return
        const isCenterAccount = this.user?.email ? this.studioAccountsMap.includes(this.user.email.toLowerCase()) : false
        this.isVerifiedOrCenter = status.verified || isCenterAccount
        this.isVerifiedOrCenter = true
        if (this.isVerifiedOrCenter) await this.fetchContracts(context)
      } catch (e) {
        if (!this.isCurrent(context)) return
        if ([401, 403].includes(e.response?.status)) this.clearContractCache(context)
        console.error("Fehler bei der Verifizierungsprüfung", e)
      } finally {
        if (this.isCurrent(context)) this.loading = false
      }
    },
    async onEmailVerified() {
      this.showVerifyDialog = false
      this.verifying = false
      this.isVerifiedOrCenter = true
      await this.loadCard()
    },
    daysInTimeSpan(timeSpan) {
      const start = moment(timeSpan.TimeSpan.Start, "YYYY-MM-DD")
      const end = moment(timeSpan.TimeSpan.End, "YYYY-MM-DD")
      return end.diff(start, "days") + 1
    },
    bonusCalculatedFromMultipleTimespans(allTimeSpans, timeSpanDays) {
      const totalDays = allTimeSpans.reduce((total, timeSpan) => {
        return total + this.daysInTimeSpan(timeSpan)
      }, 0)
      return Math.abs(totalDays - timeSpanDays) <= 1
    },
    formatTitle(text) {
      if (!text || typeof text !== "string") return text
      if (text.includes("CHF")) {
        return text.split("CHF")[0].trim()
      }
      return text
    },
    async toggleExpand(contract) {
      if (this.openProfileOnClick) {
        return this.$router.push({ path: "/user", query: { section: "membership" } })
      }
      this.expanded = !this.expanded
      if (this.expanded && contract.active) {
        this.loadUpgradesForContract(contract)
      }
    },
    onSlideChange(index) {
      this.expanded = false
      this.saveContracts()
    },
    async loadUpgradesForContract(contract) {
      if (this.upgradeData && this.upgradeData.contractID === contract.id) return
      this.loadingUpgrades = true
      this.upgradeData = null
      try {
        let userId = this.user._id || this.user.id
        let response = await api.fetchPackageUpgradePossibilitesTemp(userId, false)
        this.upgradeData = response
        this.upgradeData.contractID = contract.id
      } catch (e) {
        console.error("Upgrade load failed", e)
      } finally {
        this.loadingUpgrades = false
      }
    },
    triggerQuickUpgrade(pkg) {
      this.$emit("quick-upgrade", {
        package: pkg,
        possibilities: this.upgradeData,
      })
    },
    getAddonIcon(name) {
      if (!name) return "mdi-star"
      const n = name.toLowerCase()
      if (n.includes("solarium") || n.includes("sonne")) return "mdi-white-balance-sunny"
      if (n.includes("wasser") || n.includes("hydro") || n.includes("massage")) return "mdi-water-outline"
      if (n.includes("wellness") || n.includes("spa")) return "mdi-spa"
      if (n.includes("getränk") || n.includes("trink") || n.includes("drink")) return "mdi-bottle-soda-classic-outline"
      if (n.includes("milon") || n.includes("circle") || n.includes("zirkel")) return "mdi-chip"
      if (n.includes("pelvix") || n.includes("becken")) return "mdi-chair-rolling"
      if (n.includes("power") || n.includes("plate") || n.includes("vibra")) return "mdi-flash"
      if (n.includes("sauna")) return "mdi-hot-tub"
      if (n.includes("kinder")) return "mdi-baby-carriage"
      if (n.includes("handtuch") || n.includes("tuch")) return "mdi-tshirt-crew-outline"
      return "mdi-star-circle-outline"
    },
    async fetchContracts(context) {
      try {
        this.fetchBalance(context)
        const allExistingContracts = await api.fetchTACAllMemberships()
        if (!this.isCurrent(context)) return

        if (!allExistingContracts?.MemberDetails?.MemberDetail) {
          this.contracts = []
          this.hasSnapshot = true
          this.saveContracts()
          this.$emit("no-contracts")
          return
        }

        const rawData = allExistingContracts.MemberDetails.MemberDetail
        const memberDetails = Array.isArray(rawData) ? rawData : [rawData]

        // Temporärer Speicher für die fertigen Karten
        let finalContractsList = []

        memberDetails.forEach((detail) => {
          if (detail.ContractStartDate === detail.TerminationDate) return
          let timeSpans = detail.ContractTermTimes?.ContractTermTime
          if (!timeSpans) return
          timeSpans = Array.isArray(timeSpans) ? timeSpans : [timeSpans]

          // 1. Chronologisch sortieren (WICHTIG für die Logik)
          timeSpans.sort((a, b) => moment(a.TimeSpan.Start) - moment(b.TimeSpan.Start))

          // Alle Pausen global holen für die Berechnungs-Logik (DaysInTimeSpan etc.)
          const allIdleSpans = timeSpans.filter((ts) => ts.Type === "IDLEPERIOD")

          // Wir bauen hier temporär Karten zusammen
          let builtContracts = []
          let currentContract = null

          timeSpans.forEach((timespan) => {
            const startMoment = moment(timespan.TimeSpan.Start)
            const endMoment = moment(timespan.TimeSpan.End)
            const startFmt = startMoment.format("DD.MM.YYYY")
            const endFmt = endMoment.format("DD.MM.YYYY")

            if (timespan.Type === "DEFAULT") {
              // --- HIER IST DIE WICHTIGE ALTE LOGIK ---
              // Prüfen: Ist dieser Zeitraum nur eine Verlängerung wegen Timestop?
              const currentDuration = this.daysInTimeSpan(timespan)

              // Wir prüfen, ob die Dauer dieses Blocks exakt einer Pause entspricht
              const isTimeStopExtension =
                allIdleSpans.some((idle) => this.daysInTimeSpan(idle) === currentDuration) ||
                this.bonusCalculatedFromMultipleTimespans(allIdleSpans, currentDuration)

              // Fall A: Es ist eine Verlängerung UND wir haben schon eine offene Karte
              if (isTimeStopExtension && currentContract) {
                // Wir fügen es der "Deine Laufzeit" Liste hinzu
                currentContract.details.timeStopBonuses.push({ start: startFmt, end: endFmt })
                // Und wir verlängern das End-Datum der Karte visuell
                currentContract.end = endFmt
                currentContract.endDateObj = endMoment
                // Das MainEnd bleibt aber bestehen (die Basis-Laufzeit),
                // damit man sieht: Basis bis X, aber läuft bis Y wegen Bonus.
              }
              // Fall B: Es ist ein "echtes" neues Vertragsjahr (oder die allererste Periode)
              else {
                // Wenn schon einer offen war, speichern wir ihn ab, da er jetzt "fertig" ist
                if (currentContract) {
                  builtContracts.push(currentContract)
                }

                // NEUE KARTE STARTEN
                currentContract = {
                  id: detail.ContractID?.UniqueID[0]?.value,
                  title: detail.MemberCategoryName,
                  start: startFmt,
                  end: endFmt,
                  startDateObj: startMoment, // Zum Sortieren
                  endDateObj: endMoment,
                  renewal: detail.RenewalMode,
                  // Das Details-Objekt für das Template
                  details: {
                    mainStart: startFmt,
                    mainEnd: endFmt,
                    idlePeriods: [], // Hier kommen die Pausen rein
                    timeStopBonuses: [], // Hier kommen die Verlängerungen rein
                    regularBonuses: [],
                  },
                  addons: [], // Füllen wir später
                }
              }
            } else if (timespan.Type === "IDLEPERIOD") {
              // Pause dem aktuellen Vertrag zuordnen
              if (currentContract) {
                currentContract.details.idlePeriods.push({ start: startFmt, end: endFmt })
              }
            } else if (timespan.Type === "BONUSPERIOD") {
              // Bonus dem aktuellen Vertrag zuordnen
              if (currentContract) {
                currentContract.details.regularBonuses.push({ start: startFmt, end: endFmt })
              }
            }
          })

          // Den letzten offenen Vertrag auch noch speichern
          if (currentContract) {
            builtContracts.push(currentContract)
          }

          // --- ADDONS ZUORDNEN ---
          // Jetzt wo wir die Start/End Daten der Karten haben, verteilen wir die Pakete
          if (detail.SubContracts && detail.SubContracts.SubContract) {
            let subs = Array.isArray(detail.SubContracts.SubContract) ? detail.SubContracts.SubContract : [detail.SubContracts.SubContract]
            subs = subs.filter((sub) => sub.ContractState !== 5)
            builtContracts.forEach((contract) => {
              const cStart = contract.startDateObj
              const cEnd = contract.endDateObj

              contract.addons = subs
                .filter((sub) => {
                  const subStart = moment(sub.UsageStartDate)
                  const subEnd = moment(sub.UsageEndDate)
                  // Paket überschneidet sich mit diesem Vertragsjahr?
                  return subStart.isSameOrBefore(cEnd) && subEnd.isSameOrAfter(cStart)
                })
                .map((sub) => ({
                  name: sub.MemberCategoryName,
                  start: moment(sub.UsageStartDate).format("DD.MM.YYYY"),
                  end: moment(sub.UsageEndDate).format("DD.MM.YYYY"),
                  state: sub.ContractState,
                }))
            })
          }

          // Status (Aktiv/Zukünftig/Vergangen) setzen
          const now = moment()
          builtContracts.forEach((c) => {
            c.active = now.isBetween(c.startDateObj, c.endDateObj, "day", "[]")
            c.future = now.isBefore(c.startDateObj, "day")
            c.past = now.isAfter(c.endDateObj, "day")
            finalContractsList.push(c)
          })
        })

        const selectedKey = this.hasSnapshot ? this.contractKey(this.sortedContracts[this.carouselModel]) : null
        this.contracts = finalContractsList
        this.hasSnapshot = true

        if (this.contracts.length > 0) {
          this.$emit("contracts-loaded", true)
          // Zum aktiven springen
          const sorted = [...this.contracts].sort((a, b) => a.startDateObj - b.startDateObj)
          let initialIndex = sorted.findIndex((c) => this.contractKey(c) === selectedKey)
          if (initialIndex === -1) initialIndex = sorted.findIndex((c) => c.active)
          if (initialIndex === -1) initialIndex = 0
          this.carouselModel = initialIndex
        } else {
          this.$emit("no-contracts")
        }
        this.saveContracts()
      } catch (e) {
        if (!this.isCurrent(context)) return
        if ([401, 403].includes(e.response?.status)) this.clearContractCache(context)
        else if (!this.hasSnapshot) this.$emit("no-contracts")
        console.error("Contract error", e)
      }
    },
    async fetchBalance(context) {
      try {
        const res = await api.fetchAccountTransactionsForUser()
        if (!this.isCurrent(context)) return
        if (res && !res.error && res.balance !== undefined) {
          this.balance = { balance: res.balance }
          this.$store.commit("homeOverviewStore/save", { key: "balance", scope: context.scope, data: this.balance })
        }
      } catch (e) {
        if (this.isCurrent(context) && [401, 403].includes(e.response?.status)) this.clearContractCache(context)
      }
    },
    getCardClass(contract) {
      if (contract.future) return "future-gradient"
      if (contract.past) return "past-gradient"
      return "brand-gradient"
    },
    getStatusColor(contract) {
      if (contract.future) return "blue lighten-1"
      if (contract.past) return "grey darken-1"
      return "#52ae32"
    },
    getStatusIcon(contract) {
      if (contract.future) return "mdi-calendar-arrow-right"
      if (contract.past) return "mdi-history"
      return "mdi-check-decagram"
    },
    getStatusLabel(contract) {
      if (contract.future) return "ZUKÜNFTIG"
      if (contract.past) return "ABGELAUFEN"
      return "AKTIV"
    },
    formatBalance(transactions) {
      return transactions && transactions.balance ? parseInt(transactions.balance) : "0"
    },
    formatDecimals(transactions) {
      return transactions && transactions.balance
        ? transactions.balance.split(".")[1] && transactions.balance.split(".")[1].length === 1
          ? transactions.balance.split(".")[1] + "0"
          : transactions.balance.split(".")[1]
        : "00"
    },
  },
}
</script>
<style lang="scss" scoped>
/* VARIABLES */
$brand-green: #52ae32;
$glass-bg: rgba(255, 255, 255, 0.05);
$glass-border: rgba(255, 255, 255, 0.08);
/* Einheitliche Zeile für den Zeitstrahl */
.timeline-row {
  display: flex;
  align-items: center;
  border: 1px solid transparent; /* Platzhalter für den Rand, damit nichts springt */
  transition: all 0.2s;
}

/* Der Container für das Icon: Feste Breite zentriert das Icon */
.icon-fix {
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Typo-Hilfen */
.row-label {
  line-height: 1.1;
}
.row-value {
  font-size: 0.85rem;
}

/* Spezial-Style NUR für den Bonus */
.timeline-row.is-bonus {
  background: linear-gradient(90deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 193, 7, 0.05) 100%);
  border-color: rgba(255, 215, 0, 0.4); /* Hier wird der Rahmen sichtbar */
  position: relative;
  overflow: hidden;

  /* Der Schimmer-Effekt */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 45%, rgba(255, 255, 255, 0.1) 50%, transparent 55%);
    background-size: 200% 200%;
    animation: shine 4s infinite;
    pointer-events: none;
  }
}

@keyframes shine {
  0% {
    background-position: 200% 50%;
  }
  100% {
    background-position: -200% 50%;
  }
}
/* CARD GRADIENTS */
.brand-gradient {
  background: linear-gradient(135deg, #1e3c2f 0%, #0d1f16 100%);
  border: 1px solid rgba(82, 174, 50, 0.3);
}
.future-gradient {
  background: linear-gradient(135deg, #1a237e 0%, #000000 100%);
  border: 1px solid rgba(63, 81, 181, 0.3);
}
.past-gradient {
  background: linear-gradient(135deg, #424242 0%, #212121 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  filter: grayscale(0.9);
  opacity: 0.9;
}

/* TYPO (DER FIX) */
.contract-title {
  font-size: 1rem !important;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Max 2 Zeilen */
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
  white-space: normal; /* Erlaubt Umbruch */
}

/* Addon Styles */
.addon-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}
.addon-chip {
  padding: 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  min-height: 50px;
}

.active-addon {
  background: rgba(82, 174, 50, 0.15);
  border: 1px solid rgba(82, 174, 50, 0.3);
}
.upgrade-addon {
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.2);
  cursor: pointer;
  &:active {
    transform: scale(0.96);
    background: rgba(255, 255, 255, 0.08);
  }
}

.addon-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  &.active {
    background: $brand-green;
    box-shadow: 0 2px 8px rgba(82, 174, 50, 0.4);
  }
  &.upgrade {
    background: rgba(255, 255, 255, 0.1);
    position: relative;
  }
}

.plus-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: $brand-green;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #1e1e1e;
}
.skeleton-chip {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.addon-icon-mini {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* UTILS */
.card-shine {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 1;
}
.card-mesh {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 20px 20px;
  opacity: 0.5;
  z-index: 0;
}
.backdrop-blur {
  backdrop-filter: blur(4px);
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.opacity-70 {
  opacity: 0.7;
}
.z-index-2 {
  z-index: 2;
  position: relative;
}
.text-shadow-sm {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}
.fade-in {
  animation: fadeIn 0.5s ease-in;
}
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* BUTTONS */
.action-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s;
  &:active {
    transform: scale(0.95);
  }
  span {
    font-size: 0.65rem;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}
.action-btn {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}
.primary-glass {
  background: linear-gradient(135deg, rgba(82, 174, 50, 0.7), rgba(46, 102, 28, 0.7));
}
.secondary-glass {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.7), rgba(245, 124, 0, 0.7));
}
.health-glass {
  background: linear-gradient(135deg, rgba(233, 30, 99, 0.7), rgba(194, 24, 91, 0.7));
}
.glass-inner-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.balance-circle {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 12px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  min-width: 80px;
}

/* SKELETON & ANIMATIONS */
.custom-skeleton {
  background: rgba(255, 255, 255, 0.05) !important;
}
::v-deep .v-skeleton-loader__image {
  background: rgba(255, 255, 255, 0.05) !important;
}
.chevron-anim {
  animation: bounce 2s infinite;
}
@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(5px);
  }
  60% {
    transform: translateY(3px);
  }
}
::v-deep .v-carousel__controls {
  background: transparent;
  height: 20px;
  padding-bottom: 4px;
}
::v-deep .v-btn--active .v-btn__content .v-icon {
  color: white !important;
}
</style>
